/**
 * FidelitePage — the fidélité engine surface: the per-lab barème editor (with a paliers visual) and
 * the card accrual/reversal view. Two tabs; each panel owns its inline chargement/erreur states.
 *
 * 5-state DoD (page level): sans-droit (ProtectedRoute is empty for this module — see index.tsx —
 * so we gate here: the nav item requires finance.fidelite.read and this page renders a
 * ForbiddenState if read is missing); hors-ligne (OfflineState). The barème panel's query supplies
 * chargement/erreur; vide is impossible (the server always returns at least the config default).
 */
import { useState } from "react";
import { ForbiddenState, OfflineState, Tabs } from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { token } from "@/design/theme";
import { FINANCE_CAPS, useLabId } from "./api";
import { BaremePanel } from "./components/BaremePanel";
import { CartePanel } from "./components/CartePanel";

export function FidelitePage() {
  const { t } = useT();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canRead = useCan(FINANCE_CAPS.fideliteRead);

  const [tab, setTab] = useState("bareme");

  const header = (
    <header style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-1)" }}>
      <h1 style={{ fontSize: "var(--nv-fs-h1)", margin: 0 }}>{t("finance.fid.title")}</h1>
      <p style={{ margin: 0, color: token.slate }}>{t("finance.fid.subtitle")}</p>
    </header>
  );

  if (!canRead) {
    return <ForbiddenState requiredCapabilities={[FINANCE_CAPS.fideliteRead]} />;
  }
  if (!online) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
        {header}
        <OfflineState />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      {header}
      <Tabs
        aria-label={t("finance.fid.title")}
        value={tab}
        onChange={setTab}
        items={[
          {
            id: "bareme",
            label: t("finance.fid.tab.bareme"),
            content: <BaremePanel labId={labId} enabled />,
          },
          {
            id: "carte",
            label: t("finance.fid.tab.carte"),
            content: <CartePanel />,
          },
        ]}
      />
    </div>
  );
}
